import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, Loader2, Send, Target, Activity, CheckCircle2, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/employees/${id}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <div className="animate-fade-up py-16 text-center text-[13px] text-slate-400">Employee not found. <Link to="/owner/employees" className="text-[#1a5fd0]">Back to Capital Crew</Link></div>;
  if (!data) return <Loading />;
  const { employee, stats, active_missions, completed_missions, recent_jobs } = data;

  const assign = async () => {
    if (!instruction.trim()) return toast.error("Tell this employee what to do.");
    setBusy(true);
    try {
      const { data } = await api.post("/ceo/chat", { message: instruction, employees: [employee.id], auto_pilot: true });
      toast.success("Mission assigned & executed");
      setAssignOpen(false); setInstruction("");
      if (data?.mission?.id) navigate(`/owner/missions/${data.mission.id}`); else load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign mission."); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="employee-detail-page">
      <button onClick={() => navigate("/owner/employees")} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft size={15} /> The Capital Crew</button>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`flex h-12 w-12 items-center justify-center rounded-xl font-head text-lg font-bold ${employee.id === "controller" ? "bg-[#080d1a] text-white" : "bg-slate-100 text-slate-700"}`}>
              {employee.id === "controller" ? <Bot size={22} /> : employee.name.replace("AI ", "")[0]}
            </span>
            <div>
              <div className="font-head text-[17px] font-bold text-slate-900" data-testid="employee-name">{employee.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#b0862f]">{employee.role}</div>
              <div className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active</div>
            </div>
          </div>
          <button onClick={() => setAssignOpen(true)} data-testid="assign-mission-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#1a5fd0] px-4 py-2 text-[13px] font-bold text-white hover:bg-[#1550b8]"><Send size={14} /> Assign a mission</button>
        </div>
        <p className="mt-3 text-[13px] leading-relaxed text-slate-600">{employee.blurb}</p>
        {employee.specialties?.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 font-mono text-[9px] uppercase tracking-wider text-slate-400">Specialties / triggers</div>
            <div className="flex flex-wrap gap-1.5">{employee.specialties.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{s}</span>)}</div>
          </div>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Active missions" value={stats.active_missions} tone="blue" />
        <Stat label="Completed missions" value={stats.completed_missions} tone="green" />
        <Stat label="Jobs run" value={stats.jobs_run} tone="slate" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5" data-testid="employee-missions">
          <div className="mb-3 flex items-center gap-2"><Target size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">Missions</span></div>
          {[...active_missions, ...completed_missions].length === 0 ? (
            <div className="py-6 text-center text-[13px] text-slate-400">No missions assigned yet. Assign one above.</div>
          ) : (
            <div className="space-y-2">
              {[...active_missions, ...completed_missions].map((m) => (
                <Link key={m.id} to={`/owner/missions/${m.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 hover:bg-slate-50" data-testid="employee-mission-row">
                  <span className="truncate text-[13px] text-slate-700">{m.title}</span>
                  <span className={`ml-3 shrink-0 font-mono text-[9px] uppercase ${m.status === "Completed" ? "text-emerald-600" : "text-amber-600"}`}>{m.status}</span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="employee-jobs">
          <div className="mb-3 flex items-center gap-2"><Activity size={15} className="text-[#b0862f]" /><span className="font-head text-[14px] font-bold text-slate-900">Recent work</span></div>
          {recent_jobs.length === 0 ? (
            <div className="py-6 text-center text-[13px] text-slate-400">No completed work yet.</div>
          ) : (
            <div className="space-y-3">
              {recent_jobs.map((j, i) => (
                <div key={i} className="rounded-xl border border-slate-100 p-3" data-testid="employee-job">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate text-[12px] font-semibold text-slate-800">{j.task}</span>
                    {j.model && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[9px] text-slate-500">{j.model}</span>}
                  </div>
                  <div className="prose prose-sm line-clamp-4 max-w-none text-[12px] text-slate-500"><ReactMarkdown>{j.result}</ReactMarkdown></div>
                  <div className="mt-1 font-mono text-[9px] text-slate-400">{new Date(j.when).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="assign-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setAssignOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-center justify-between"><h3 className="font-head text-lg font-extrabold text-slate-900">Assign a mission to {employee.name}</h3><button onClick={() => setAssignOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={4} data-testid="assign-instruction"
              className="w-full rounded-lg border border-slate-200 p-3 text-[14px] outline-none focus:border-[#1a5fd0]" placeholder={`e.g. ${employee.role === "Marketing" ? "Draft a launch announcement for our new funding product." : "Complete this task and report back."}`} />
            <button onClick={assign} disabled={busy} data-testid="assign-submit-btn" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#080d1a] py-2.5 text-[14px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} {busy ? "Assigning & running…" : "Assign & run"}
            </button>
            <p className="mt-2 text-center text-[11px] text-slate-400">Creates a real mission, runs it through your AI workforce, and opens the result.</p>
          </div>
        </div>
      )}
    </div>
  );
}
